import { useEffect, useRef } from 'react';
import { GameEvent, GameState } from '../../types/game';
import { makeHostTakeoverEvent } from '../../domain/events';
import { eventStore } from '../../services/EventStore';
import { saveHostFlag } from '../../utils/hostFlag';
import { resolveHostTakeoverEnabled } from '../../utils/featureFlags';
import { debugLog } from '../debug/debugLog';
import { isVerboseLoggingEnabled, logger } from '../../utils/logger';

interface UseHostTakeoverParams {
  gameState: GameState;
  isHost: boolean;
  currentClientPlayerId: string | null;
  setIsHost: (value: boolean) => void;
}

const HOST_STUCK_THRESHOLD_MS = 5 * 60_000;
const HOST_TAKEOVER_POLL_MS = 30_000;

type GameEventWithId = Pick<GameEvent, 'id' | 'type' | 'timestamp'>;

const log = (label: string, payload: Record<string, unknown>) => {
  if (isVerboseLoggingEnabled()) {
    logger.debug('HOST_TAKEOVER', { label, ...payload });
  }
  debugLog(`hostTakeover:${label}`, payload, { source: 'useHostTakeover' });
};

function getArrivalMap(events: GameEventWithId[], map: Map<string, number>) {
  const now = Date.now();
  for (const evt of events) {
    if (!evt.id) continue;
    if (!map.has(evt.id)) {
      map.set(evt.id, now);
    }
  }
  const visible = new Set(events.map((evt) => evt.id).filter(Boolean) as string[]);
  for (const key of map.keys()) {
    if (!visible.has(key)) {
      map.delete(key);
    }
  }
  return map;
}

const RELEVANT_EVENT_TYPES: GameEvent['type'][] = [
  'DICE_ROLL',
  'DICE_KEEP',
  'TURN_END',
  'HOST_TAKEOVER',
];

function getLastRelevantArrival(events: GameEventWithId[], arrivals: Map<string, number>) {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const evt = events[i];
    if (!evt) continue;
    if (RELEVANT_EVENT_TYPES.includes(evt.type)) {
      const arrival = evt.id ? arrivals.get(evt.id) : undefined;
      if (typeof arrival === 'number') {
        return { arrival, type: evt.type } as const;
      }
      if (evt.timestamp) {
        const parsed = new Date(evt.timestamp).getTime();
        if (!Number.isNaN(parsed)) {
          return { arrival: parsed, type: evt.type } as const;
        }
      }
      return { arrival: undefined, type: evt.type } as const;
    }
  }
  return { arrival: undefined, type: undefined } as const;
}

export function useHostTakeover({ gameState, isHost, currentClientPlayerId, setIsHost }: UseHostTakeoverParams) {
  const featureEnabled = resolveHostTakeoverEnabled();
  const arrivalTimesRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!featureEnabled) return;
    if (!Array.isArray(gameState.events)) return;
    getArrivalMap(gameState.events as GameEventWithId[], arrivalTimesRef.current);
  }, [featureEnabled, gameState.events]);

  useEffect(() => {
    if (!featureEnabled) return;
    if (gameState.mode === 'REAL') return;
    if (isHost) return;
    if (!gameState.id) return;
    if (!currentClientPlayerId) return;
    if (!gameState.players.some((p) => p.id === currentClientPlayerId)) return;

    const evaluateStaleness = () => {
      if (!gameState.turnCycle?.activePlayerId) {
        return { stale: false as const };
      }
      const events = (gameState.events || []) as GameEventWithId[];
      const { arrival, type } = getLastRelevantArrival(events, arrivalTimesRef.current);
      if (typeof arrival !== 'number') {
        return { stale: false as const, reason: 'no-arrival', type };
      }
      const diff = Date.now() - arrival;
      const stale = diff > HOST_STUCK_THRESHOLD_MS;
      return { stale, diff, arrival, type } as const;
    };

    const attemptTakeover = async () => {
      if (isHost) return;
      const result = evaluateStaleness();
      if (!result.stale) {
        log('skip', {
          activePlayerId: gameState.turnCycle?.activePlayerId,
          currentClientPlayerId,
          ...result,
        });
        return;
      }
      log('trigger', {
        activePlayerId: gameState.turnCycle?.activePlayerId,
        currentClientPlayerId,
        diff: result.diff,
        lastEventType: result.type,
      });
      try {
        const takeoverEvent = makeHostTakeoverEvent(currentClientPlayerId, 'TIMEOUT');
        await eventStore.appendEvent(gameState.id, takeoverEvent);
        setIsHost(true);
        saveHostFlag(true);
      } catch (error) {
        logger.warn('HOST_TAKEOVER_ERROR', (error as Error)?.message ?? error);
      }
    };

    const interval = setInterval(attemptTakeover, HOST_TAKEOVER_POLL_MS);
    return () => clearInterval(interval);
  }, [featureEnabled, gameState.mode, gameState.id, gameState.players, gameState.events, gameState.turnCycle?.activePlayerId, isHost, currentClientPlayerId, setIsHost]);
}

